import { ExternalId, Score } from '@/interfaces/titles';

export interface Scoring {
	name: string;
	provider: string;
	queryName: string; // name of query parameter to be mapped from
	append: string;
	decimals: number;
	image: string;
	step: number;
	min: number;
	max: number;
	value: number;
	score?: string;
	link?: string;
	filterDisplay?: boolean;
}

export const SCORINGS: Scoring[] = [
	{
		name: 'JustWatch Rating',
		queryName: '',
		provider: 'justwatch:rating',
		append: '%',
		decimals: 0,
		step: 1,
		min: 0,
		max: 100,
		value: 0,
		image: `/${ASSETS_DIR}/img/jw-icon.png`,
		filterDisplay: false,
	},
	{
		name: 'IMDB',
		queryName: 'rating_imdb',
		provider: 'imdb:score',
		append: '',
		decimals: 1,
		step: 0.1,
		min: 0,
		max: 10,
		value: 0,
		image: `/${ASSETS_DIR}/img/imdb-logo.png`,
		filterDisplay: true,
	},
];

const JUSTWATCH_SCORING = [
	{
		provider: 'imdb:score',
		max: 10,
		weight: 1,
	},
	{
		provider: 'tmdb:score',
		max: 10,
		weight: 1,
	},
	// ,
	// {
	// 	provider: 'tmdb:popularity',
	// 	max: 10,
	// 	weight: 1
	// }
];

export function calculateJustwatchRating(ratings: Score[]): number | null {
	let totalScore = 0;
	let weight = 0;
	let popularity = 0;
	for (const rating of ratings) {
		for (const jwScoring of JUSTWATCH_SCORING) {
			if (jwScoring.provider === rating.provider_type) {
				weight += jwScoring.weight;
				totalScore += (rating.value / jwScoring.max) * jwScoring.weight;
			}
		}

		if (rating.provider_type === 'tmdb:popularity') {
			popularity = rating.value;
		}
	}
	return clamp(Math.floor((totalScore / weight) * 100 + (Math.log(250 * popularity + 1) - 1)), 1, 100);
}

export function getTotalScoreVotes(ratings: Score[] = []): number {
	const totalVotes = ratings
		.filter(score => score.provider_type.endsWith(':votes'))
		.map(score => score.value)
		.reduce((acc, curr) => acc + curr, 0);

	// TODO: This is very temporary and only until 'tmdb:votes' is added.
	// ratingCount must be positive but this can return 0 if one of the sources (imdb or tmdb) is missing ':votes'.
	// in this case I return 2, counting each source as one vote.
	if (totalVotes <= 0) return 2;

	return totalVotes;
}

function clamp(number: number, min: number, max: number): number | null {
	if (isFinite(number)) {
		return Math.min(Math.max(number, min), max);
	} else {
		return null;
	}
}

/**
 * Return filtered (i.e. allowed) and processed (i.e. rounded) scorings.
 * Also holds the JustWatch scoring in the first element.
 *
 * @param {object[]} scorings array with scorings for a specific title
 * @param {string[]} excluded what scorings to leave out
 * @returns {object[]} array with processed scorings
 */
export function getScorings(
	scorings: Score[],
	externalIds: ExternalId[],
	excluded: string[] = [],
	includeLinks = false
): Scoring[] {
	const filteredScorings = [];

	// REGULAR SCORINGS
	for (const accepted of SCORINGS) {
		for (const score of scorings) {
			if (score.provider_type === accepted.provider && excluded.indexOf(accepted.provider) === -1) {
				const value = { ...accepted };
				value.score = score?.value?.toFixed(accepted.decimals);
				// insert a IMDB link out if there is a 'imdb:id'
				if (accepted.provider === 'imdb:score') {
					externalIds
						.filter(eid => eid.provider === 'imdb')
						.filter(() => includeLinks)
						.forEach(eid => {
							value.link = `https://www.imdb.com/title/${eid.external_id}/?ref_=justwatch`;
						});
				}
				filteredScorings.push(value);
			}
		}
	}

	// JUSTWATCH SCORING
	const justWatchScoring = SCORINGS[0];
	if (scorings && scorings.length > 1 && excluded.indexOf(justWatchScoring.provider) === -1) {
		const score = calculateJustwatchRating(scorings);
		if (score !== null && score >= 0) {
			filteredScorings.unshift({
				...justWatchScoring,
				score: score.toString(),
			});
		}
	}

	return filteredScorings;
}

export function getVotesLabel(votes: number) {
	let suffix = 'k';
	let votesToDisplay = votes / 1000;

	if (votesToDisplay < 1) {
		return null;
	}

	if (votesToDisplay >= 1000) {
		votesToDisplay = votesToDisplay / 1000;
		suffix = 'm';
	}

	return Math.floor(votesToDisplay) + suffix;
}
